import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toastErr, toastOk } from '@dd/ui';
import { api, errText } from './api';

type Q = Record<string, string | number | boolean | undefined | null>;

export function useGet<T>(path: string, query?: Q, enabled = true) {
  return useQuery({ queryKey: [path, query ?? {}] as QueryKey, queryFn: () => api.get<T>(path, query), enabled });
}

/** cursor pages: the API returns { items, nextCursor } */
export function usePaged<T>(path: string, query?: Q, enabled = true) {
  const q = useInfiniteQuery({
    queryKey: [path, query ?? {}, 'paged'] as QueryKey, enabled, initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.get<{ items: T[]; nextCursor?: string | null }>(path, { ...(query ?? {}), cursor: pageParam }),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  return { ...q, items: (q.data?.pages ?? []).flatMap((p) => p.items) };
}

/** a write: toast the result, then refresh whatever was on screen */
export function useAct<A = void, R = unknown>(fn: (a: A) => Promise<R>, ok?: string | ((r: R) => string), after?: (r: R) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (r) => { toastOk(typeof ok === 'function' ? ok(r) : ok ?? 'Done'); void qc.invalidateQueries(); after?.(r); },
    onError: (e) => toastErr(errText(e)),
  });
}
